import { createRoot } from 'react-dom/client'

// アプリケーション名と対応するモジュールのマッピング
const applications: Record<string, () => Promise<React.ComponentType>> = {
  contact: () => import('./forms/apps/contact/app').then((mod) => mod.default),
  downloads: () =>
    import('./forms/apps/downloads/app').then((mod) => mod.default),
  seminar: () => import('./forms/apps/seminar/app').then((mod) => mod.default),
}

/**
 * WordPress 上に配置された React アプリケーションを起動する。
 * テンプレート側はアプリケーションのルートとなる要素に `data-react-app` 属性を指定し、その名前に対応するモジュールを遅延ロードして初期化する。
 * 利用可能なアプリケーションは、 `applications` オブジェクトで定義されており、 アプリケーションが見つからない場合は、 コンソールにエラーを出力する。
 *
 * @example
 * <div data-react-app="contact"></div> => <ContactApp /> がマウントされる
 * <div data-react-app="unknown"></div> => コンソールにエラーが出力される
 */
export function bootstrapReactApps() {
  window.addEventListener('mount-react-app', async (event) => {
    const { app } = (event as CustomEvent<{ app: string }>).detail

    if (!app || !!applications[app]) {
      console.error(
        `Invalid React application name: ${app}. Available applications: ${Object.keys(applications).join(', ')}.`,
      )
      return
    }

    const element = document.querySelector<HTMLElement>(
      `[data-react-app="${app}"]`,
    )

    if (!element) {
      console.error(
        `React application element not found: [data-react-app="${app}"].`,
      )
      return
    }

    await mountReactApp(element)
  })

  documentReady().then(() => {
    document
      .querySelectorAll<HTMLElement>('[data-react-app]')
      .forEach(mountReactApp)
  })

  /**
   * 対象要素に React アプリケーションをマウントする。
   *
   * @param element - マウント対象の要素
   */
  async function mountReactApp(element: HTMLElement) {
    const appName = element.dataset.reactApp ?? ''
    const AppPromise = applications[appName]

    if (!AppPromise) {
      console.error(
        `React application ${appName} not found. Available applications: ${Object.keys(
          applications,
        ).join(', ')}.`,
      )
      return
    }

    const App = await AppPromise()
    const root = createRoot(element)

    root.render(<App />)
  }
}

function documentReady(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resolve(), {
        once: true,
      })
    } else {
      resolve()
    }
  })
}
